/**
 * AI Readiness Scoring Engine
 *
 * All scoring functions are pure and deterministic.
 * Given the same inputs, they always produce the same outputs.
 */

export type Dimension =
  | "code-generation"
  | "testing"
  | "data-pipeline"
  | "devops"
  | "documentation"
  | "customer-facing";

export interface DimensionScore {
  dimension: Dimension;
  score: number; // 0-100
  grade: string; // A-F
  rationale: string;
  factors: ScoringFactor[];
}

export interface ScoringFactor {
  name: string;
  contribution: number; // -20 to +20
  reason: string;
}

export interface OverallScore {
  composite: number; // 0-100
  grade: string;
  dimensions: DimensionScore[];
}

export const DIMENSION_LABELS: Record<Dimension, string> = {
  "code-generation": "Code Generation & Assistance",
  testing: "Testing & QA",
  "data-pipeline": "Data Pipeline & Analytics",
  devops: "DevOps & Infrastructure",
  documentation: "Documentation & Knowledge",
  "customer-facing": "Customer-Facing AI Features",
};

export const DEFAULT_WEIGHTS: Record<Dimension, number> = {
  "code-generation": 0.2,
  testing: 0.18,
  "data-pipeline": 0.15,
  devops: 0.15,
  documentation: 0.12,
  "customer-facing": 0.2,
};

export function scoreToGrade(score: number): string {
  if (score >= 90) return "A";
  if (score >= 80) return "B";
  if (score >= 70) return "C";
  if (score >= 60) return "D";
  return "F";
}

export function clampScore(score: number): number {
  return Math.max(0, Math.min(100, Math.round(score)));
}

export function weightedAverage(
  scores: Record<Dimension, number>,
  weights: Record<Dimension, number> = DEFAULT_WEIGHTS,
): number {
  let total = 0;
  let weightSum = 0;

  for (const dim of Object.keys(weights) as Dimension[]) {
    const score = scores[dim] ?? 0;
    const weight = weights[dim] ?? 0;
    total += score * weight;
    weightSum += weight;
  }

  if (weightSum === 0) return 0;
  return clampScore(total / weightSum);
}
